import { apiIndex } from '@/api/report/report';
import { StaleStateReport } from '@/api/report/stateReportTransport';

// MainPage normally remounts on every query change. A State-view toggle must
// retain the report's filters, refs and in-flight response sequence instead.
export function stateReportViewKey(route, router, company_id) {
	const record = route.matched && route.matched[route.matched.length - 1];
	const component = record && record.components.default;
	const options = component && (component.options || component);
	if (company_id !== 'beautyplus' || !options || !options.mixins || !options.mixins.some(mixin => mixin.state_report_view)) return route.fullPath;
	const query = { ...route.query };
	delete query.restriction_only;
	return router.resolve({ path: route.path, query, hash: route.hash }).route.fullPath;
}

/** Per-screen explicit scope, request sequencing and output lifetime. Ordinary/non-beautyplus calls are unchanged. */
export default function stateReport(options) {
	return {
		state_report_view: true,
		data() {
			return {
				state_report_id: options.id,
				restriction_only_available: false,
				restriction_only: false,
				state_scope_read_only: false,
				state_report_pending: false,
				state_report_sequence: 0,
				state_report_request: null,
				state_report_ready: null,
				state_report_response_read_only: false,
				state_report_filters: null,
				state_report_outputs: [],
				state_report_ordinary_filters: {},
			};
		},
		provide() {
			return { state_report_context: this };
		},
		computed: {
			state_report_locked() {
				return this.state_scope_read_only || this.restriction_only || this.state_report_pending;
			},
		},
		watch: {
			'$route.query.restriction_only'(value) {
				if (!this.restriction_only_available || (value === 'true') === this.restriction_only) return;
				this.restriction_only = value === 'true';
				return this.onStateReportChange();
			},
		},
		methods: {
			async initializeStateReport() {
				const user = this.$session.get('user');
				if (!user || user.company_id !== 'beautyplus') return;
				const { data } = await apiIndex('?state_report=' + options.id);
				this.restriction_only_available = data.restriction_only_available === true;
				if (this.restriction_only_available && this.$route.query.restriction_only === 'true') {
					this.restriction_only = true;
					this.state_report_pending = true;
					const response = await apiIndex('?state_report=' + options.id + '&restriction_only=true');
					this.state_report_filters = response.data;
				}
			},
			stateReportQuery(query) {
				return this.state_scope_read_only ? { ...query, restriction_only: 'true' } : query;
			},
			captureStateReportOutput() {
				if (this.state_report_pending) return null;
				const user = this.$session.get('user');
				if (!user || user.company_id !== 'beautyplus' || !this.restriction_only_available) return { valid: true, read_only: false };
				const context = { valid: true, read_only: this.state_scope_read_only };
				this.state_report_outputs.push(context);
				return context;
			},
			invalidateStateReportOutputs() {
				const current = this.$store && this.$store.state && this.$store.state.report;
				if (current && this.state_report_outputs.includes(current.state_scope_context)) this.$store.commit('setShowModal', false);
				this.state_report_outputs.forEach(output => {
					output.valid = false;
					if (output.cancel) output.cancel();
				});
				this.state_report_outputs = [];
			},
			async onStateReportChange() {
				const sequence = ++this.state_report_sequence;
				this.state_report_pending = true;
				this.invalidateStateReportOutputs();
				for (const key of options.results) {
					if (Array.isArray(this[key])) this[key] = [];
					else if (this[key] && typeof this[key] === 'object') this[key] = {};
					else if (typeof this[key] === 'number') this[key] = 0;
				}
				if ('current_ar_as_of' in this) this.current_ar_as_of = null;
				if ('current_ar_time_zone' in this) this.current_ar_time_zone = null;
				if ('showModal' in this) this.showModal = false;
				if ('sheet_popup' in this) this.sheet_popup = null;
				if (this.restriction_only) {
					const { data } = await apiIndex('?state_report=' + options.id + '&restriction_only=true');
					if (sequence !== this.state_report_sequence) return;
					this.restriction_only_available = data.restriction_only_available === true;
					if (!this.restriction_only_available) return;
					this.state_report_filters = data;
					for (const [key, source] of [
						['salesGroups', 'restrictionOnlySalesGroupList'],
						['states', 'restrictionOnlyStateList'],
					]) {
						if (key in this) {
							if (!(key in this.state_report_ordinary_filters)) this.state_report_ordinary_filters[key] = this[key];
							this[key] = data[source] || [];
						}
					}
				} else {
					for (const key of Object.keys(this.state_report_ordinary_filters)) this[key] = this.state_report_ordinary_filters[key];
				}
				const query = { ...this.$route.query };
				if (this.restriction_only) query.restriction_only = 'true';
				else delete query.restriction_only;
				await this.$router.replace({ query }).catch(() => {});
				if (sequence !== this.state_report_sequence) return;
				await this[options.search]();
			},
		},
		created() {
			this.state_report_ready = this.initializeStateReport();
			for (const name of options.queries) {
				const original = this[name];
				if (!original) continue;
				this[name] = async (...args) => {
					const user = this.$session.get('user');
					if (!user || user.company_id !== 'beautyplus') return original(...args);
					await this.state_report_ready;
					if (!this.restriction_only_available || !this.state_report_locked) {
						try {
							return await original(...args);
						} catch (error) {
							if (!(error instanceof StaleStateReport)) throw error;
							return;
						}
					}
					const sequence = ++this.state_report_sequence;
					this.invalidateStateReportOutputs();
					this.state_report_pending = true;
					this.state_report_request = { restriction_only: this.restriction_only };
					this.state_report_response_read_only = this.state_scope_read_only;
					try {
						const result = await original(...args);
						if (sequence !== this.state_report_sequence) return;
						this.state_scope_read_only = this.state_report_response_read_only;
						this.state_report_pending = false;
						return result;
					} catch (error) {
						if (!(error instanceof StaleStateReport)) throw error;
					} finally {
						if (sequence === this.state_report_sequence) this.state_report_request = null;
					}
				};
			}
		},
		beforeDestroy() {
			++this.state_report_sequence;
			this.invalidateStateReportOutputs();
		},
	};
}
